import React, {useEffect, useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";
import usePermissions from "../../../hooks/usePermissions";
import {
    apiErrorMessage,
    DAY_KEYS,
    DAY_LABELS,
    fetchCalendarHours,
    formatHour,
    timeSlots,
    updateCalendarHours,
} from "./calendarSettingsApi";
import {Panel} from "../../../stock/StockUI";
import {SettingsForm, SaveAction, ListLoader} from "../SettingsUI";
import AppleSelect from "../../Common/AppleSelect";

export const CALENDAR_HOURS_KEY = ["calendar-hours"];

// Tablets' defaults (POS Réglages > Calendrier): every day open 9h-20h.
const buildDefaultHours = () =>
    DAY_KEYS.reduce((acc, day) => {
        acc[day] = {closed: false, start: 9, end: 20};
        return acc;
    }, {});

const OPENING_SLOTS = timeSlots(9, 20);

const OPENING_OPTIONS = OPENING_SLOTS.map((v) => ({value: v, label: formatHour(v)}));

// Réglages > Calendrier > Horaires d'ouverture: open/closed + hours per
// weekday. Closed days block new RDVs on the tablets and are greyed out on
// their calendar; the hours size the calendar grid.
const CalendarHoursSettings = () => {
    const {canUpdate} = usePermissions();
    const canManage = canUpdate("settings");
    const queryClient = useQueryClient();
    const {data: savedHours, isLoading, isError} = useQuery({
        queryKey: CALENDAR_HOURS_KEY,
        queryFn: fetchCalendarHours,
        staleTime: 0,
        refetchOnMount: "always",
    });
    const [hours, setHours] = useState(null);

    useEffect(() => {
        if (!isLoading && savedHours !== undefined) {
            setHours({...buildDefaultHours(), ...(savedHours || {})});
        }
    }, [savedHours, isLoading]);

    const invalidDay = useMemo(
        () => hours && DAY_KEYS.find((day) => !hours[day].closed && hours[day].end <= hours[day].start),
        [hours]
    );

    const mutation = useMutation({
        mutationFn: () => updateCalendarHours(hours),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: CALENDAR_HOURS_KEY});
            toast.success("Horaires enregistrés — envoyés aux caisses");
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible d'enregistrer les horaires")),
    });

    const setDay = (day, changes) => setHours((prev) => ({...prev, [day]: {...prev[day], ...changes}}));

    if (isError && savedHours === undefined) {
        return <p className="text-sm text-red-500 p-6">Impossible de charger les horaires.</p>;
    }
    if (isLoading || !hours) {
        return <ListLoader label="Chargement des horaires…"/>;
    }

    return (
        <SettingsForm>
            {canManage && <SaveAction onClick={() => mutation.mutate()} saving={mutation.isPending} disabled={!!invalidDay}/>}

            {savedHours === null && (
                <p className="text-sm text-blue-800 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
                    Aucun horaire n'est encore défini ici : chaque caisse garde ses propres réglages
                    jusqu'au premier enregistrement.
                </p>
            )}

            <Panel title="Horaires d'ouverture">
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {DAY_KEYS.map((day) => {
                        const dayHours = hours[day];
                        const open = !dayHours.closed;
                        return (
                            <div key={day} className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-3">
                                <span className="w-24 text-sm font-semibold text-gray-900 dark:text-gray-100">{DAY_LABELS[day]}</span>

                                <label className={`flex items-center gap-2 ${canManage ? "cursor-pointer" : "opacity-60"}`}>
                                    <span className="relative inline-flex">
                                        <input
                                            type="checkbox"
                                            className="peer sr-only"
                                            checked={open}
                                            disabled={!canManage}
                                            onChange={(e) => setDay(day, {closed: !e.target.checked})}
                                        />
                                        <span className="w-11 h-6 rounded-full bg-gray-300 dark:bg-gray-600 peer-checked:bg-primary transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2"/>
                                        <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"/>
                                    </span>
                                    <span className={`text-sm ${open ? "text-gray-700 dark:text-gray-300" : "text-gray-400"}`}>
                                        {open ? "Ouvert" : "Fermé (repos)"}
                                    </span>
                                </label>

                                {open && (
                                    <div className="flex items-center gap-2 ml-auto">
                                        <AppleSelect
                                            size="sm"
                                            aria-label={`Ouverture ${DAY_LABELS[day]}`}
                                            value={dayHours.start}
                                            disabled={!canManage}
                                            options={OPENING_OPTIONS}
                                            onChange={(e) => setDay(day, {start: Number(e.target.value)})}
                                        />
                                        <span className="text-gray-400 text-sm">à</span>
                                        <AppleSelect
                                            size="sm"
                                            aria-label={`Fermeture ${DAY_LABELS[day]}`}
                                            value={dayHours.end}
                                            disabled={!canManage}
                                            options={OPENING_OPTIONS}
                                            onChange={(e) => setDay(day, {end: Number(e.target.value)})}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Panel>

            {invalidDay && (
                <p className="text-sm text-red-500">
                    {DAY_LABELS[invalidDay]} : l'heure de fermeture doit être après l'heure d'ouverture.
                </p>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400">
                Les jours fermés bloquent la prise de rendez-vous sur les caisses. Les modifications
                sont envoyées automatiquement aux caisses connectées.
            </p>
        </SettingsForm>
    );
};

export default CalendarHoursSettings;
